// Display helpers shared by the web red-flag screens (Dashboard → Red Flag tab, the
// Red Flags verification page, the Appeal Workflow report).
//
// Rules are configurable per project, so which rules exist, how each one gates and
// what it is called all come from the API. The LEGACY_* constants here describe the
// six built-in rules as they behaved before that, and are used ONLY as fallbacks for
// an older payload that does not carry the rule's own metadata.

/** How a rule turns occurrences into red flags (mirrors the API's TriggerMode). */
export type RedFlagTriggerMode = 'immediate' | 'every_nth' | 'once_at_nth';
/** Comparator of a value metric's condition (mirrors the API's ConditionOp). */
export type RedFlagConditionOp = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq';

/** FALLBACK: the built-in rules that gate (1 red flag per N occurrences in a month). */
export const LEGACY_GATED_FLAG_TYPES: ReadonlySet<string> = new Set(['missing_signout', 'late_signin', 'missed_random']);
export const LEGACY_GATED_THRESHOLD = 5;
export const LEGACY_GATED_THRESHOLD_RANDOM = 10;
/** FALLBACK N for a built-in gated rule (missed random attendance counts per slot). */
export const legacyGatedThresholdFor = (code: string): number =>
  code === 'missed_random' ? LEGACY_GATED_THRESHOLD_RANDOM : LEGACY_GATED_THRESHOLD;

/** The gating fields a rule descriptor may carry. All optional: older payloads omit them. */
export interface RuleGateMeta {
  /** Legacy-compatible N: the rule's N when gated; an immediate rule carries the legacy 5. */
  threshold?: number;
  /** The rule's own N (1 for immediate). */
  ruleThreshold?: number;
  mode?: RedFlagTriggerMode;
  gated?: boolean;
  kind?: 'system' | 'custom';
}

/** Does this rule accumulate occurrences before it flags? API `gated` → `mode` → legacy set. */
export const isGatedRule = (code: string, rule?: RuleGateMeta | null): boolean => {
  if (typeof rule?.gated === 'boolean') return rule.gated;
  if (rule?.mode) return rule.mode !== 'immediate';
  return LEGACY_GATED_FLAG_TYPES.has(code);
};

const posInt = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v >= 1 ? Math.floor(v) : null;

/** N for a gated rule (1 for an immediate one): ruleThreshold → legacy threshold → constants. */
export const ruleThresholdOf = (code: string, rule?: RuleGateMeta | null): number => {
  if (!isGatedRule(code, rule)) return 1;
  return posInt(rule?.ruleThreshold) ?? posInt(rule?.threshold) ?? legacyGatedThresholdFor(code);
};

/** One person's own gating of a rule: sent only for months saved per role. */
export interface OwnGateMeta {
  ruleThreshold?: number;
  mode?: RedFlagTriggerMode;
}

/**
 * A rule as it gates for ONE person. In a month saved per role a column's rule carries
 * the parameter's smallest N (the strictest any role uses) while each person's entry
 * carries their own N and mode, so a Promoter on every 5th must not read "1 per 3".
 * Without them (rules set by audience, the built-in rules, older payloads) the column's
 * rule comes back as it is — the same object — so those screens do not change.
 */
export const personalGateMeta = <T extends RuleGateMeta>(rule: T | undefined, own?: OwnGateMeta | null): T | undefined => {
  const n = posInt(own?.ruleThreshold);
  const mode = own?.mode;
  if (n === null && !mode) return rule;
  const out = { ...(rule ?? {}) } as T;
  if (mode) {
    out.mode = mode;
    out.gated = mode !== 'immediate';
  }
  if (n !== null) out.ruleThreshold = n;
  return out;
};

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th", 22 → "22nd". */
export const ordinal = (n: number): string => {
  const v = Math.abs(n) % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (Math.abs(n) % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

/** The gate in a few words, for tooltips: "1 per 5" / "once, on the 3rd". */
export const gateText = (code: string, rule?: RuleGateMeta | null): string => {
  const n = ruleThresholdOf(code, rule);
  return rule?.mode === 'once_at_nth' ? `once, on the ${ordinal(n)}` : `1 per ${n}`;
};

/** Column-header tooltip describing how a rule flags. */
export const ruleHeaderTitle = (code: string, rule?: RuleGateMeta | null): string => {
  const custom = rule?.kind === 'custom' ? ' · Custom rule' : '';
  if (!isGatedRule(code, rule)) return `Immediate — flags on first occurrence${custom}`;
  const n = ruleThresholdOf(code, rule);
  return rule?.mode === 'once_at_nth'
    ? `Gated — flags once, on the ${ordinal(n)} occurrence in a calendar month${custom}`
    : `Gated — 1 red flag per ${n} occurrence(s) in a calendar month${custom}`;
};

// ── The built-in six, unchanged ──────────────────────────────────────────────

/** How each built-in rule gated before rules were configurable (every project's rules while the feature is dark). */
const LEGACY_RULE_GATES: Readonly<Record<string, { mode: RedFlagTriggerMode; n: number }>> = {
  grooming: { mode: 'immediate', n: 1 },
  uninformed_absence: { mode: 'immediate', n: 1 },
  missed_training: { mode: 'immediate', n: 1 },
  missing_signout: { mode: 'every_nth', n: LEGACY_GATED_THRESHOLD },
  late_signin: { mode: 'every_nth', n: LEGACY_GATED_THRESHOLD },
  missed_random: { mode: 'every_nth', n: LEGACY_GATED_THRESHOLD_RANDOM },
};

const hasRuleMeta = (r: RuleGateMeta): boolean =>
  r.mode !== undefined || r.gated !== undefined || r.ruleThreshold !== undefined || r.kind !== undefined;

/**
 * Are these rule columns exactly the built-in six, gating as they always did? True for
 * an older payload that carries no rule metadata at all, and for the built-in set at its
 * built-in modes and thresholds (what every project runs while Red Flag Rules is dark).
 * Screens then keep their original copy word for word; anything else is described from
 * the rules themselves.
 */
export const isLegacyRuleList = (
  rules: ReadonlyArray<{ key: string; label?: string | null } & RuleGateMeta> | null | undefined,
): boolean => {
  if (!Array.isArray(rules) || rules.length === 0) return false;
  if (!rules.some(hasRuleMeta)) return true;
  const codes = Object.keys(LEGACY_RULE_GATES);
  if (rules.length !== codes.length || new Set(rules.map((r) => r.key)).size !== codes.length) return false;
  return rules.every((r) => {
    const legacy = LEGACY_RULE_GATES[r.key];
    if (!legacy) return false;
    if (r.kind !== undefined && r.kind !== 'system') return false;
    if (r.mode !== undefined && r.mode !== legacy.mode) return false;
    if (isGatedRule(r.key, r) !== (legacy.mode !== 'immediate')) return false;
    return legacy.mode === 'immediate' || ruleThresholdOf(r.key, r) === legacy.n;
  });
};

/** The column-header tooltip the verification page has always shown for the built-in six. */
export const legacyRuleHeaderTitle = (code: string, rule?: { threshold?: number } | null): string =>
  LEGACY_GATED_FLAG_TYPES.has(code)
    ? `Gated — flags on every ${rule?.threshold ?? legacyGatedThresholdFor(code)} occurrence(s) in this range`
    : 'Immediate — flags on first occurrence';

// ── Colours ──────────────────────────────────────────────────────────────────

/** Fixed colours of the six built-in rules. */
export const STANDARD_FLAG_HEX: Readonly<Record<string, string>> = {
  grooming: '#8b5cf6',
  uninformed_absence: '#ef4444',
  missed_training: '#f59e0b',
  missing_signout: '#0ea5e9',
  late_signin: '#ec4899',
  missed_random: '#14b8a6',
};
/** The palette an unknown non-custom code (e.g. yellow_escalation) has always taken BY POSITION. */
export const LEGACY_POSITION_PALETTE: readonly string[] = ['#22c55e', '#6366f1', '#f97316', '#06b6d4', '#d946ef', '#84cc16'];
const EXTRA_FLAG_PALETTE = [...LEGACY_POSITION_PALETTE, '#a16207', '#78716c', '#1d4ed8', '#7e22ce'];

/** A custom parameter's code: `c_<id>`. */
export const isCustomFlagCode = (key: string): boolean => /^c_\d+$/.test(key);

/** FNV-1a over the key's UTF-16 code units — deterministic across renders and sessions. */
export const hashFlagKey = (key: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

/**
 * A flag type's colour. A built-in rule keeps its fixed colour, and a custom `c_<id>` is
 * coloured by a hash of its key, never by its position, so it matches in the chart,
 * legend and every row's chips. Any other code (yellow_escalation…) keeps the colour it
 * always had: the legacy palette by its `index` in the list being drawn (by hash when
 * no index is given).
 */
export const flagTypeHex = (key: string, index?: number): string => {
  if (STANDARD_FLAG_HEX[key]) return STANDARD_FLAG_HEX[key];
  if (!isCustomFlagCode(key) && typeof index === 'number' && index >= 0) {
    return LEGACY_POSITION_PALETTE[index % LEGACY_POSITION_PALETTE.length];
  }
  return EXTRA_FLAG_PALETTE[hashFlagKey(key) % EXTRA_FLAG_PALETTE.length];
};

// ── Labels / type lists ──────────────────────────────────────────────────────

export interface FlagTypeMeta { key: string; label: string }

/** A type's label, falling back to its key — never blank, never "undefined". */
export const flagTypeLabel = (t: { key: string; label?: string | null }): string =>
  (t.label && String(t.label).trim()) || t.key;

/**
 * Merge a freshly loaded type list into the known one. An UNFILTERED response lists
 * the month's whole rule set, so it replaces the list. A response fetched WITH a type
 * filter lists only that type, so it may only ADD (starting from `fallback` when
 * nothing is known yet) — otherwise picking a type would shrink the list to it.
 * `null` types = unavailable: the known list is kept. Returns `prev` when unchanged.
 */
export const mergeFlagTypeList = (
  prev: FlagTypeMeta[] | null,
  types: FlagTypeMeta[] | null,
  requestedFlagType: string | undefined,
  fallback: FlagTypeMeta[],
): FlagTypeMeta[] | null => {
  if (!types) return prev;
  const clean = types.filter((t) => t && typeof t.key === 'string' && t.key.trim() !== '');
  if (!requestedFlagType) return clean;
  const base = prev ?? fallback;
  const have = new Set(base.map((t) => t.key));
  const extra = clean.filter((t) => !have.has(t.key));
  return extra.length ? [...base, ...extra] : prev;
};

// ── Machine voids (ledger) ───────────────────────────────────────────────────

/** `excused_by` values starting 'system:' are engine voids, not a reviewer's excusal. */
export const isSystemRemoval = (by: string | null | undefined): boolean =>
  typeof by === 'string' && by.startsWith('system:');

export const systemRemovalLabel = (by: string | null | undefined): string =>
  by === 'system:settle' ? 'Removed — source data changed' : 'Removed by the system';

// ── Custom-rule evidence ─────────────────────────────────────────────────────

export const CONDITION_OP_WORDS: Readonly<Record<RedFlagConditionOp, string>> = {
  lt: 'below',
  lte: 'at or below',
  gt: 'above',
  gte: 'at or above',
  eq: 'exactly',
  neq: 'anything but',
};

/** 45 + '%' → "45%", 4 + '/10' → "4/10", 3.5 + 'hours' → "3.5 hours". */
export const fmtMetricValue = (v: number | null | undefined, unit?: string | null): string => {
  if (v == null || !Number.isFinite(v)) return '—';
  const n = v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const u = (unit ?? '').trim();
  if (!u) return n;
  return /^[A-Za-z]/.test(u) ? `${n} ${u}` : `${n}${u}`;
};

/** "counts when below 4/10", or null for an event metric (no condition). */
export const conditionText = (
  condition: { op: RedFlagConditionOp; value: number } | null | undefined,
  unit?: string | null,
): string | null =>
  condition ? `counts when ${CONDITION_OP_WORDS[condition.op] ?? condition.op} ${fmtMetricValue(condition.value, unit)}` : null;

/**
 * How a custom rule gates, in days: custom rules count one per DAY the behaviour
 * happens (never per item), whatever the metric's unit.
 */
export const customGateLine = (ev: { gated?: boolean; mode?: RedFlagTriggerMode; threshold?: number }): string => {
  const n = Math.max(1, Math.floor(Number(ev.threshold) || 1));
  if (!ev.gated || !ev.mode || ev.mode === 'immediate') return 'A red flag on every day this happens.';
  return ev.mode === 'once_at_nth'
    ? `One red flag a month, on the ${ordinal(n)} day this happens.`
    : `A red flag on every ${ordinal(n)} day this happens in a calendar month.`;
};
